import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FooterLinks } from "@/components/FooterLinks";
import { PrivacyControls } from "@/components/PrivacyControls";
import api, { formatApiError } from "@/lib/api";
import { billingSourceLabel } from "@/lib/adapt";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Bell, KeyRound, Trash2, MailWarning, CheckCircle2, Crown, Mail, ChevronRight, RotateCcw, ShieldAlert } from "lucide-react";

function GroupLabel({ children }) {
  return <p className="mb-2 mt-6 px-1 text-xs font-semibold uppercase tracking-wider text-slate-400">{children}</p>;
}
function Row({ icon: Icon, iconClass = "text-emerald-700", title, subtitle, onClick, danger, right, testid }) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp type={onClick ? "button" : undefined} onClick={onClick} data-testid={testid}
      className={`flex w-full items-center gap-3 rounded-2xl border border-emerald-900/10 bg-white p-4 text-left shadow-sm transition-colors ${onClick ? "hover:bg-mint/40" : ""}`}>
      {Icon && <Icon className={`h-5 w-5 shrink-0 ${danger ? "text-red-500" : iconClass}`} />}
      <div className="min-w-0 flex-1">
        <p className={`truncate font-semibold ${danger ? "text-red-600" : "text-emerald-950"}`}>{title}</p>
        {subtitle && <p className="truncate text-sm text-slate-500">{subtitle}</p>}
      </div>
      {right}
      {onClick && !right && <ChevronRight className="h-5 w-5 shrink-0 text-slate-300" />}
    </Comp>
  );
}

export default function Settings() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState(null);
  const [pw, setPw] = useState({ currentPassword: "", newPassword: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  useEffect(() => {
    (async () => { try { const { data } = await api.get("/notifications/prefs"); setPrefs(data.prefs || data || {}); } catch { setPrefs({}); } })();
  }, []);

  const setPref = async (k, v) => {
    setPrefs((p) => ({ ...p, [k]: v }));
    try { await api.put("/notifications/prefs", { [k]: v }); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const changePassword = async (e) => {
    e.preventDefault(); setSavingPw(true);
    try { await api.post("/account/change-password", pw); toast.success("Password changed"); setPw({ currentPassword: "", newPassword: "" }); setPwOpen(false); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingPw(false); }
  };

  const deleteAccount = async () => {
    try { await api.post("/account/delete", {}); toast.success("Account deleted"); await logout(); navigate("/"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const sub = user?.subscription || {};
  const verified = !!user?.email_verified;

  return (
    <div className="mx-auto max-w-xl">
      <h1 className="font-serif text-3xl font-bold text-emerald-950">Account &amp; security</h1>
      <p className="mt-1 text-sm text-slate-500">Manage your email, password and subscription</p>

      {/* Email address */}
      <GroupLabel>Email address</GroupLabel>
      <Row
        icon={Mail}
        title={user?.email}
        subtitle={verified ? "Verified · read-only" : "Not verified yet"}
        testid="account-email-row"
        right={verified
          ? <span data-testid="email-verified-badge" className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-600 text-white"><CheckCircle2 className="h-4 w-4" /></span>
          : <Button onClick={() => navigate("/verify-email")} data-testid="verify-email-btn" size="sm" className="rounded-full bg-amber-600 text-white hover:bg-amber-700">Verify now</Button>}
      />
      {!verified && (
        <button onClick={() => navigate("/verify-email")} data-testid="email-unverified-note" className="mt-2 flex w-full items-start gap-2 rounded-2xl bg-amber-50 p-3 text-left text-sm text-amber-800 transition-colors hover:bg-amber-100">
          <MailWarning className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Verify your email to secure your account and earn your verified badge. We'll send a 6-digit code to <b>{user?.email}</b>.</span>
        </button>
      )}

      {!user?.is_assistant && (<><GroupLabel>Privacy controls</GroupLabel><PrivacyControls /></>)}

      {/* Security */}
      <GroupLabel>Security</GroupLabel>
      <div className="space-y-3">
        <Row icon={KeyRound} title="Change Password" subtitle="Update your account password" onClick={() => setPwOpen(true)} testid="open-change-password" />
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button data-testid="delete-account-btn" className="flex w-full items-center gap-3 rounded-2xl border border-red-200 bg-white p-4 text-left shadow-sm transition-colors hover:bg-red-50">
              <Trash2 className="h-5 w-5 shrink-0 text-red-500" />
              <div className="min-w-0 flex-1"><p className="font-semibold text-red-600">Delete Account</p><p className="truncate text-sm text-slate-500">Permanently remove your account &amp; data</p></div>
              <ChevronRight className="h-5 w-5 shrink-0 text-red-200" />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-red-500" /> Delete your account?</AlertDialogTitle><AlertDialogDescription>This permanently removes your profile from Nikah Match across web, Android and iOS. This action is irreversible.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel data-testid="cancel-delete">Cancel</AlertDialogCancel><AlertDialogAction onClick={deleteAccount} data-testid="confirm-delete" className="bg-red-600 hover:bg-red-700">Delete forever</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      {/* Subscription */}
      <GroupLabel>Subscription</GroupLabel>
      {user?.is_premium ? (
        <Row icon={Crown} iconClass="text-amber-500" title="Premium active" subtitle={`Billing: ${billingSourceLabel(sub.source)}${sub.reason ? ` · ${sub.reason}` : ""}`} testid="subscription-status" />
      ) : (
        <Row icon={Crown} iconClass="text-amber-500" title="Upgrade to Premium" subtitle="Unlock unlimited interests & AI features" onClick={() => navigate("/premium")} testid="subscription-upgrade" />
      )}
      <div className="mt-2">
        <Row icon={RotateCcw} title="Restore Purchases" subtitle="Recover Premium after reinstalling or switching devices" onClick={async () => { await refreshUser(); toast.success(user?.is_premium ? "Premium restored" : "No active purchases found for this account"); }} testid="restore-purchases-btn" />
      </div>

      {/* Notifications */}
      <GroupLabel>Notifications</GroupLabel>
      <div className="rounded-2xl border border-emerald-900/10 bg-white p-5 shadow-sm">
        <div className="mb-3 flex items-center gap-2 font-semibold text-emerald-900"><Bell className="h-4 w-4 text-emerald-700" /> Email &amp; push preferences</div>
        {prefs === null ? <Loader2 className="h-5 w-5 animate-spin text-emerald-700" /> : (
          <div className="space-y-4">
            {[["interests", "New interest received"], ["mutualMatches", "New mutual match"], ["successStories", "Success stories"], ["subscription", "Subscription updates"]].map(([k, label]) => (
              <div key={k} className="flex items-center justify-between"><Label>{label}</Label><Switch checked={!!prefs[k]} onCheckedChange={(v) => setPref(k, v)} data-testid={`notif-pref-${k}`} /></div>
            ))}
          </div>
        )}
      </div>

      <div className="py-8 text-center text-xs text-slate-400">
        <FooterLinks className="flex flex-wrap justify-center gap-x-4 gap-y-2" linkClassName="hover:text-emerald-700" />
      </div>

      {/* Change password dialog */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2 font-serif text-emerald-950"><KeyRound className="h-5 w-5 text-emerald-700" /> Change password</DialogTitle></DialogHeader>
          <form onSubmit={changePassword} className="space-y-4" data-testid="change-password-form">
            <div className="space-y-2"><Label>Current password</Label><Input type="password" required value={pw.currentPassword} onChange={(e) => setPw({ ...pw, currentPassword: e.target.value })} data-testid="current-password-input" className="rounded-xl" /></div>
            <div className="space-y-2"><Label>New password</Label><Input type="password" required minLength={8} value={pw.newPassword} onChange={(e) => setPw({ ...pw, newPassword: e.target.value })} data-testid="new-password-input" className="rounded-xl" /><p className="text-xs text-slate-400">At least 8 characters.</p></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPwOpen(false)} className="rounded-full">Cancel</Button>
              <Button type="submit" disabled={savingPw} data-testid="change-password-btn" className="rounded-full bg-emerald-800 text-white hover:bg-emerald-900">{savingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
